import React, { useState, useEffect, useRef } from 'react';
import './MusicPlayer.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMusic,
  faPlay,
  faPause,
  faForwardStep,
  faBackwardStep,
  faVolumeHigh,
  faVolumeLow,
  faVolumeXmark,
  faListUl,
  faUpRightFromSquare,
  faRepeat
} from '@fortawesome/free-solid-svg-icons';

const YOUTUBE_PLAYLIST_URL = 'https://www.youtube.com/playlist?list=PLdg0nPcr3qrw';
const SOUNDCLOUD_PLAYLIST_URL = 'https://soundcloud.com/atlanticrecords/sets/gtavithealbum';
const SOUNDCLOUD_PLAYLIST_ID = '2300288925';
const SOUNDCLOUD_EMBED_SRC = `https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/${SOUNDCLOUD_PLAYLIST_ID}&color=%23ff5cc2&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&continuous_play=true`;

const DEFAULT_TRACKS = [
  {
    title: "That's It (feat. Future & Metro Boomin)",
    artist: 'Yung Lean',
    duration: 174,
    artwork: 'https://i1.sndcdn.com/artworks-zzFl8vqxgsAtCI5y-oyvRJA-t500x500.jpg'
  },
  {
    title: 'RHYNO (prod. Guy-Manuel de Homem-Christo)',
    artist: 'Travis Scott',
    duration: 198,
    artwork: 'https://i1.sndcdn.com/artworks-zzFl8vqxgsAtCI5y-oyvRJA-t500x500.jpg'
  },
  {
    title: 'Sexy Magic',
    artist: 'CA7RIEL, Paco Amoroso, PinkPantheress, Fred again..',
    duration: 165,
    artwork: 'https://i1.sndcdn.com/artworks-zzFl8vqxgsAtCI5y-oyvRJA-t500x500.jpg'
  },
  {
    title: 'Last Thing You Need',
    artist: 'Morgan Wallen',
    duration: 182,
    artwork: 'https://i1.sndcdn.com/artworks-zzFl8vqxgsAtCI5y-oyvRJA-t500x500.jpg'
  },
  {
    title: 'Suzuki',
    artist: 'Fuerza Regida',
    duration: 190,
    artwork: 'https://i1.sndcdn.com/artworks-zzFl8vqxgsAtCI5y-oyvRJA-t500x500.jpg'
  },
  {
    title: 'Macacoa 2000',
    artist: 'Rauw Alejandro',
    duration: 173,
    artwork: 'https://i1.sndcdn.com/artworks-zzFl8vqxgsAtCI5y-oyvRJA-t500x500.jpg'
  },
  {
    title: 'Don\'t Chart',
    artist: 'Cardi B',
    duration: 164,
    artwork: 'https://i1.sndcdn.com/artworks-zzFl8vqxgsAtCI5y-oyvRJA-t500x500.jpg'
  },
  {
    title: 'Bright Lights, Big City',
    artist: 'Keith Richards',
    duration: 210,
    artwork: 'https://i1.sndcdn.com/artworks-zzFl8vqxgsAtCI5y-oyvRJA-t500x500.jpg'
  }
];

const MusicPlayer = ({
  isOpen,
  onClose,
  onToggle,
  isFooterVisible,
  showToggle = true,
  showControls = true
}) => {
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(DEFAULT_TRACKS[0].duration);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('gta_album_volume');
    return saved !== null ? parseInt(saved, 10) : 80;
  });
  const [isMuted, setIsMuted] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const [showTracklist, setShowTracklist] = useState(false);
  const [tracks, setTracks] = useState(DEFAULT_TRACKS);
  const [currentSoundInfo, setCurrentSoundInfo] = useState(null);

  const iframeRef = useRef(null);
  const widgetRef = useRef(null);
  const volumeRef = useRef(volume);
  const tracksRef = useRef(tracks);
  const isRepeatRef = useRef(isRepeat);
  const currentTrackIndexRef = useRef(currentTrackIndex);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  useEffect(() => {
    tracksRef.current = tracks;
  }, [tracks]);

  useEffect(() => {
    isRepeatRef.current = isRepeat;
  }, [isRepeat]);

  useEffect(() => {
    currentTrackIndexRef.current = currentTrackIndex;
  }, [currentTrackIndex]);

  useEffect(() => {
    const setupWidget = () => {
      if (!iframeRef.current || !window.SC || !window.SC.Widget) return;

      try {
        const widget = window.SC.Widget(iframeRef.current);
        widgetRef.current = widget;

        widget.bind(window.SC.Widget.Events.READY, () => {
          setIsReady(true);
          widget.setVolume(volumeRef.current);

          widget.getSounds((soundList) => {
            if (soundList && soundList.length > 0) {
              const formatted = soundList.map((s, idx) => {
                const defaultTrack = DEFAULT_TRACKS[idx] || DEFAULT_TRACKS[0];
                const artwork = s.artwork_url ? s.artwork_url.replace('-large', '-t500x500') : defaultTrack.artwork;
                return {
                  title: defaultTrack.title,
                  artist: defaultTrack.artist,
                  duration: s.duration ? Math.floor(s.duration / 1000) : defaultTrack.duration,
                  artwork
                };
              });
              setTracks(formatted);
            }
          });

          widget.getCurrentSound((sound) => {
            if (sound) {
              widget.getCurrentSoundIndex((idx) => {
                const validIdx = typeof idx === 'number' && idx >= 0 && idx < DEFAULT_TRACKS.length ? idx : 0;
                setCurrentTrackIndex(validIdx);
                const currentDefault = DEFAULT_TRACKS[validIdx];
                const artwork = sound.artwork_url ? sound.artwork_url.replace('-large', '-t500x500') : currentDefault.artwork;
                setCurrentSoundInfo({
                  title: currentDefault.title,
                  artist: currentDefault.artist,
                  artwork
                });
                if (sound.duration) setDuration(Math.floor(sound.duration / 1000));
              });
            }
          });
        });

        widget.bind(window.SC.Widget.Events.PLAY, () => {
          setIsPlaying(true);
          widget.getCurrentSoundIndex((idx) => {
            const validIdx = typeof idx === 'number' && idx >= 0 && idx < DEFAULT_TRACKS.length ? idx : 0;
            setCurrentTrackIndex(validIdx);
            const currentDefault = DEFAULT_TRACKS[validIdx];
            widget.getCurrentSound((sound) => {
              const artwork = sound && sound.artwork_url ? sound.artwork_url.replace('-large', '-t500x500') : currentDefault.artwork;
              setCurrentSoundInfo({
                title: currentDefault.title,
                artist: currentDefault.artist,
                artwork
              });
              if (sound && sound.duration) setDuration(Math.floor(sound.duration / 1000));
            });
          });
        });

        widget.bind(window.SC.Widget.Events.PAUSE, () => {
          setIsPlaying(false);
        });

        widget.bind(window.SC.Widget.Events.FINISH, () => {
          if (isRepeatRef.current) {
            const targetIdx = currentTrackIndexRef.current;
            setTimeout(() => {
              widget.skip(targetIdx);
              widget.seekTo(0);
              widget.play();
              setCurrentTime(0);
            }, 50);
          } else {
            setIsPlaying(false);
          }
        });

        widget.bind(window.SC.Widget.Events.PLAY_PROGRESS, (progress) => {
          if (progress && typeof progress.currentPosition === 'number') {
            setCurrentTime(progress.currentPosition / 1000);
          }
        });
      } catch (err) {
        console.warn('SoundCloud Widget error:', err);
      }
    };

    if (!window.SC || !window.SC.Widget) {
      const existingScript = document.getElementById('soundcloud-widget-api');
      if (!existingScript) {
        const script = document.createElement('script');
        script.id = 'soundcloud-widget-api';
        script.src = 'https://w.soundcloud.com/player/api.js';
        script.onload = () => {
          setupWidget();
        };
        document.body.appendChild(script);
      } else {
        const interval = setInterval(() => {
          if (window.SC && window.SC.Widget) {
            clearInterval(interval);
            setupWidget();
          }
        }, 300);
      }
    } else {
      setupWidget();
    }
  }, []);

  const activeDefault = DEFAULT_TRACKS[currentTrackIndex] || DEFAULT_TRACKS[0];
  const activeTrack = tracks[currentTrackIndex] || activeDefault;
  const displayTitle = activeDefault.title || activeTrack.title || currentSoundInfo?.title;
  const displayArtist = activeDefault.artist;
  const displayArtwork = currentSoundInfo?.artwork || activeTrack.artwork || activeDefault.artwork;

  const handlePlayPause = () => {
    if (!widgetRef.current) return;
    try {
      if (isPlaying) {
        setIsPlaying(false);
        widgetRef.current.pause();
      } else {
        setIsPlaying(true);
        widgetRef.current.play();
      }
    } catch (e) {
      widgetRef.current.toggle();
    }
  };

  const handleNext = () => {
    if (!widgetRef.current) return;
    try {
      widgetRef.current.getCurrentSoundIndex((idx) => {
        const currentIdx = typeof idx === 'number' ? idx : currentTrackIndex;
        const nextIdx = (currentIdx + 1) % tracks.length;
        setCurrentTrackIndex(nextIdx);
        setCurrentTime(0);
        setIsPlaying(true);
        widgetRef.current.skip(nextIdx);
        setTimeout(() => {
          if (widgetRef.current) {
            widgetRef.current.seekTo(0);
            widgetRef.current.play();
          }
        }, 80);
        setTimeout(() => {
          if (widgetRef.current) {
            widgetRef.current.seekTo(0);
          }
        }, 250);
      });
    } catch (e) {
      widgetRef.current.next();
      setTimeout(() => {
        if (widgetRef.current) {
          widgetRef.current.seekTo(0);
          widgetRef.current.play();
        }
      }, 80);
      setCurrentTime(0);
      setIsPlaying(true);
    }
  };

  const handlePrevious = () => {
    if (!widgetRef.current) return;
    if (currentTime > 3) {
      widgetRef.current.seekTo(0);
      setCurrentTime(0);
      return;
    }
    try {
      widgetRef.current.getCurrentSoundIndex((idx) => {
        const currentIdx = typeof idx === 'number' ? idx : currentTrackIndex;
        const prevIdx = (currentIdx - 1 + tracks.length) % tracks.length;
        setCurrentTrackIndex(prevIdx);
        setCurrentTime(0);
        setIsPlaying(true);
        widgetRef.current.skip(prevIdx);
        setTimeout(() => {
          if (widgetRef.current) {
            widgetRef.current.seekTo(0);
            widgetRef.current.play();
          }
        }, 80);
        setTimeout(() => {
          if (widgetRef.current) {
            widgetRef.current.seekTo(0);
          }
        }, 250);
      });
    } catch (e) {
      widgetRef.current.prev();
      setTimeout(() => {
        if (widgetRef.current) {
          widgetRef.current.seekTo(0);
          widgetRef.current.play();
        }
      }, 80);
      setCurrentTime(0);
      setIsPlaying(true);
    }
  };

  const handleSelectTrack = (index) => {
    if (!widgetRef.current) return;
    try {
      setCurrentTrackIndex(index);
      setCurrentTime(0);
      setIsPlaying(true);
      widgetRef.current.skip(index);
      setTimeout(() => {
        if (widgetRef.current) {
          widgetRef.current.seekTo(0);
          widgetRef.current.play();
        }
      }, 80);
      setTimeout(() => {
        if (widgetRef.current) {
          widgetRef.current.seekTo(0);
        }
      }, 250);
    } catch (e) {
      console.warn('Skip to track error:', e);
    }
  };

  const handleToggleMute = () => {
    if (!widgetRef.current) return;
    if (isMuted) {
      widgetRef.current.setVolume(volume);
      setIsMuted(false);
    } else {
      widgetRef.current.setVolume(0);
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseInt(e.target.value, 10);
    setVolume(val);
    localStorage.setItem('gta_album_volume', val);

    if (widgetRef.current) {
      widgetRef.current.setVolume(val);
      if (val === 0) setIsMuted(true);
      else if (isMuted) setIsMuted(false);
    }
  };

  const handleSeekChange = (e) => {
    const secs = parseFloat(e.target.value);
    setCurrentTime(secs);
    if (widgetRef.current) {
      widgetRef.current.seekTo(secs * 1000);
    }
  };

  const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const progressPercent = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  return (
    <>
      {showToggle && (
        <button
          onClick={onToggle}
          className={`music-player-toggle ${showControls || isOpen || isPlaying ? 'visible' : ''} ${isFooterVisible ? 'footer-visible' : ''
            } ${isPlaying ? 'is-playing' : ''}`}
          aria-label="Toggle GTA VI Music Player"
          title="GTA VI: The Album Audio Player"
        >
          <FontAwesomeIcon icon={faMusic} />

          {isPlaying && (
            <div className="music-toggle-eq" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
        </button>
      )}

      <div className="music-sc-frame-container collapsed" aria-hidden="true">
        <iframe
          ref={iframeRef}
          id="sc-widget-iframe"
          width="100%"
          height="166"
          scrolling="no"
          frameBorder="no"
          allow="autoplay; encrypted-media"
          src={SOUNDCLOUD_EMBED_SRC}
          title="GTA VI The Album Audio Stream"
        ></iframe>
      </div>

      <div
        className={`music-player-panel ${isOpen ? 'open' : ''} ${isFooterVisible ? 'footer-visible' : ''
          }`}
        role="region"
        aria-label="GTA VI Audio Player"
      >
        <div className="music-player-header">
          <h2 className="music-header-title">GTA VI: THE ALBUM</h2>
        </div>

        <div className="music-art-wrapper">
          <div className="music-vinyl-container">
            <div className={`music-vinyl-disc ${isPlaying ? 'spinning' : ''}`}>
              <div className="music-vinyl-center">
                <div className="music-vinyl-hole"></div>
              </div>
            </div>
            <div className="music-cover-art">
              <img
                src={displayArtwork}
                alt={displayTitle}
                className="music-cover-img"
                onError={(e) => {
                  e.target.src = `${process.env.PUBLIC_URL}/img/backgrounds/Official_Cover_Art_landscape.webp`;
                }}
              />
            </div>
          </div>
        </div>

        <div className="music-track-info">
          <h3 className="music-track-title" title={displayTitle}>
            {displayTitle}
          </h3>
          <p className="music-track-artist" title={displayArtist}>
            {displayArtist}
          </p>
        </div>

        <div className="music-progress-section">
          <div className="music-progress-bar-container">
            <div
              className="music-progress-fill"
              style={{ width: `${progressPercent}%` }}
            ></div>
            <input
              type="range"
              min="0"
              max={duration || 100}
              step="0.5"
              value={currentTime}
              onChange={handleSeekChange}
              className="music-scrubber-input"
              aria-label="Seek track"
            />
          </div>
          <div className="music-time-display">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <div className="music-primary-controls">
          <button
            onClick={handlePrevious}
            className="music-ctrl-btn"
            title="Previous track"
            aria-label="Previous track"
          >
            <FontAwesomeIcon icon={faBackwardStep} />
          </button>

          <button
            onClick={handlePlayPause}
            className={`music-play-btn ${!isReady ? 'loading' : ''}`}
            title={!isReady ? 'Loading SoundCloud player...' : isPlaying ? 'Pause' : 'Play'}
            aria-label={isPlaying ? 'Pause music' : 'Play music'}
          >
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </button>

          <button
            onClick={handleNext}
            className="music-ctrl-btn"
            title="Next track"
            aria-label="Next track"
          >
            <FontAwesomeIcon icon={faForwardStep} />
          </button>

          <button
            onClick={() => setIsRepeat(!isRepeat)}
            className={`music-ctrl-btn ${isRepeat ? 'active' : ''}`}
            title={isRepeat ? 'Repeat Song: On' : 'Repeat Song: Off'}
            aria-label={isRepeat ? 'Disable repeat' : 'Enable repeat'}
          >
            <FontAwesomeIcon icon={faRepeat} />
          </button>
        </div>

        <div className="music-secondary-controls">
          <div className="music-volume-box">
            <button
              onClick={handleToggleMute}
              className="music-volume-btn"
              title={isMuted ? 'Unmute' : 'Mute'}
              aria-label="Toggle mute"
            >
              <FontAwesomeIcon
                icon={isMuted || volume === 0 ? faVolumeXmark : volume < 50 ? faVolumeLow : faVolumeHigh}
              />
            </button>
            <input
              type="range"
              min="0"
              max="100"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="music-volume-slider"
              aria-label="Volume slider"
            />
          </div>

          <div className="music-extra-actions">
            <a
              href={YOUTUBE_PLAYLIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="music-action-btn"
              title="Open playlist on YouTube"
              aria-label="Open playlist on YouTube"
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.5 12 3.5 12 3.5s-7.505 0-9.377.55a3.016 3.016 0 0 0-2.122 2.136C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.55 9.376.55 9.376.55s7.505 0 9.377-.55a3.016 3.016 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
              <span>YouTube</span>
            </a>

            <button
              onClick={() => setShowTracklist(!showTracklist)}
              className={`music-action-btn ${showTracklist ? 'active' : ''}`}
              title="View tracklist"
              aria-label="Toggle tracklist"
            >
              <FontAwesomeIcon icon={faListUl} />
              <span>Tracks</span>
            </button>

            <a
              href={SOUNDCLOUD_PLAYLIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="music-action-btn"
              title="Open playlist on SoundCloud"
              aria-label="Open playlist on SoundCloud"
            >
              <FontAwesomeIcon icon={faUpRightFromSquare} />
            </a>
          </div>
        </div>

        {showTracklist && (
          <div className="music-tracklist-drawer">
            {tracks.map((track, idx) => (
              <div
                key={idx}
                onClick={() => handleSelectTrack(idx)}
                className={`music-track-item ${idx === currentTrackIndex ? 'current' : ''}`}
              >
                <div className="music-track-item-left">
                  <span className="music-track-num">{idx + 1}</span>
                  <div className="music-track-details">
                    <span className="music-track-name">{DEFAULT_TRACKS[idx]?.title || track.title}</span>
                    <span className="music-track-by">{DEFAULT_TRACKS[idx]?.artist || track.artist}</span>
                  </div>
                </div>
                {idx === currentTrackIndex && (
                  <div
                    className={`music-track-eq ${isPlaying ? 'is-playing' : 'is-paused'}`}
                    aria-label={isPlaying ? 'Playing' : 'Selected'}
                  >
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default MusicPlayer;
